import { ClipBoardTooltip } from "@/components/ClipBoardTooltip.tsx";
import { ContentPane } from "@/components/ContentPane.tsx";
import { NavigateAfterDelete } from "@/components/NavigateAfterDelete.tsx";
import { ResourceLink } from "@/components/ResourceLink.tsx";
import { ResourceWithIcon } from "@/components/ResourceWithIcon.tsx";
import { SectionHeader } from "@/components/SectionHeader.tsx";
import { Separator } from "@/components/Separator.tsx";
import { Spacer } from "@/components/Spacer.tsx";
import { ViewTitle } from "@/components/Title.tsx";
import { Toolbar } from "@/components/Toolbar.tsx";
import { WTabs } from "@/components/WTabs.tsx";
import { DeleteButton } from "@/components/button/DeleteButton.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { LongDate } from "@/components/l10n/DateFormat.tsx";
import { DetailsHolder, DetailsTable } from "@/components/table/DetailsTable.tsx";
import { DetailsTableBillingAccount } from "@/components/table/DetailsTableBillingAccount.tsx";
import { DetailsTableIpAddress } from "@/components/table/DetailsTableIpAddress.tsx";
import { MonthlyCostRow } from "@/components/table/DetailsTableMonthlyCost.tsx";
import { DetailsTableName } from "@/components/table/DetailsTableName.tsx";
import { DetailsTableRow } from "@/components/table/DetailsTableRow.tsx";
import { WTable, WTableBody } from "@/components/table/WTable.tsx";
import { getResourceById } from "@/modules/api/resourceTypeException.tsx";
import { getResourceDescription } from "@/modules/api/resourceTypes.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { LocationDetail } from "@/modules/location/LocationDetail.tsx";
import { VpcRow } from "@/modules/network/VpcRow.tsx";
import type { AssignedIpAddress } from "@/modules/network/ipAddress/assignable.ts";
import * as ipQuery from "@/modules/network/ipAddress/ipAddressApi.ts";
import { assignedLbsQueryAtom, type LoadBalancerWithAssigned } from "@/modules/network/ipAddress/joinAssignedQuery.ts";
import { AddForwardingRuleForm } from "@/modules/network/loadbalancer/AddForwardingRuleForm.tsx";
import { AddTargetServerForm } from "@/modules/network/loadbalancer/AddTargetServerForm.tsx";
import { LoadBalancerDeleteModal } from "@/modules/network/loadbalancer/LoadBalancerDeleteModal.tsx";
import { convertTargetType } from "@/modules/network/loadbalancer/loadBalancer.logic.ts";
import {
    changeLoadBalancerBillingAccountMutation,
    deleteLoadBalancerMutation,
    dropForwardingRuleFromLoadBalancerMutation,
    renameLoadBalancerMutation,
    unlinkTargetFromLoadBalancerMutation,
    type LoadBalancerWithType,
} from "@/modules/network/loadbalancer/loadBalancerApi.ts";
import { lbTargetsAtom } from "@/modules/network/loadbalancer/targets.ts";
import { vpcQueryAtom } from "@/modules/network/vpc/vpcApi.ts";
import { ResourceStatusBadge } from "@/modules/resource/ResourceStatusBadge.tsx";
import { getResourceName } from "@/modules/resource/getResourceName.ts";
import { useDeletableResource } from "@/utils/query/useDeletableResource.tsx";
import { useQueryResultAtom, useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import type { BillingAccount } from "@warrenio/api-spec/spec.oats.gen";
import { useState } from "react";

// NB: Extract the mutation call to a separate function so we can get its exact type via `typeof`
function useDeleteLoadBalancerMutation() {
    return useStandardMutation(deleteLoadBalancerMutation);
}

export type DeleteLoadBalancerMutation = ReturnType<typeof useDeleteLoadBalancerMutation>;

export function LoadBalancerView({ location, lbId }: { location: string; lbId: string }) {
    //#region Hooks
    const { t } = useLingui();
    const deleteMutation = useDeleteLoadBalancerMutation();
    const renameMutation = useStandardMutation(renameLoadBalancerMutation);
    const changeBillingAccountMutation = useStandardMutation(changeLoadBalancerBillingAccountMutation);

    const data = useSuspenseQueryAtom(assignedLbsQueryAtom(location));
    const vpcs = useSuspenseQueryAtom(vpcQueryAtom(location));

    const balancer = useDeletableResource(() => getResourceById(data, lbId, "load_balancer"), deleteMutation);
    //#endregion

    if (balancer === undefined) {
        return <NavigateAfterDelete />;
    }

    const { $type, uuid, display_name, private_address, created_at, billing_account_id, network_uuid, price } =
        balancer;

    const vpc = vpcs.get(network_uuid);

    async function changeBillingAccount(billing_account: BillingAccount) {
        await changeBillingAccountMutation.mutateAsync({ location, uuid, billing_account_id: billing_account.id });
    }

    async function renameLoadBalancer(display_name: string) {
        await renameMutation.mutateAsync({ location, uuid, body: { display_name } });
    }

    return (
        <>
            <Toolbar>
                <LoadBalancerDeleteModal item={balancer} deleteMutation={deleteMutation} />
            </Toolbar>

            <ContentPane className="VStack gap-4">
                <ViewTitle title={display_name} />

                <DetailsHolder>
                    <DetailsTable>
                        <DetailsTableName value={display_name} onChange={async (e) => await renameLoadBalancer(e)} />
                        <DetailsTableRow title={t`Type`}>
                            <ResourceWithIcon type={$type} />
                        </DetailsTableRow>

                        <LoadBalancerPublicIp item={balancer} />

                        <DetailsTableRow title={t`Private IPv4`}>
                            <ClipBoardTooltip>{private_address}</ClipBoardTooltip>
                        </DetailsTableRow>

                        <VpcRow vpc={vpc} />
                    </DetailsTable>

                    <Spacer />

                    <DetailsTable>
                        <DetailsTableRow title={t`UUID`}>
                            <ClipBoardTooltip>{uuid}</ClipBoardTooltip>
                        </DetailsTableRow>
                        <DetailsTableRow title={t`Created`}>
                            <LongDate date={created_at} />
                        </DetailsTableRow>
                        <LocationDetail slug={location} />
                        <DetailsTableRow />
                        <DetailsTableBillingAccount
                            valueKey={billing_account_id}
                            onChange={async (item) => await changeBillingAccount(item.account)}
                        />
                        <MonthlyCostRow price={price} billingAccountId={billing_account_id} />
                    </DetailsTable>
                </DetailsHolder>
            </ContentPane>

            <Separator />

            <WTabs
                allTab
                items={[
                    {
                        id: "target",
                        title: <Trans>Target servers</Trans>,
                        content: <TargetServersContent item={balancer} />,
                    },
                    {
                        id: "rules",
                        title: <Trans>Forwarding rules</Trans>,
                        content: <ForwardingRulesContent item={balancer} />,
                    },
                ]}
            >
                <ContentPane className="VStack gap-4">
                    <SectionHeader title={<Trans>Description</Trans>} />
                    <div>{getResourceDescription("load_balancer")}</div>
                </ContentPane>
            </WTabs>
        </>
    );
}

function LoadBalancerPublicIp({ item }: { item: LoadBalancerWithAssigned }) {
    //#region Hooks
    const unAssignIpMutation = useStandardMutation(ipQuery.unassignIpAddressMutation);
    const assignIpAddressMutation = useStandardMutation(ipQuery.assignIpAddressMutation);
    //#endregion

    const { location, assignedPublicIp, uuid } = item;

    async function assignIpAddress(address: AssignedIpAddress | null) {
        if (address) {
            await assignIpAddressMutation.mutateAsync({
                location,
                address: address.address,
                body: {
                    assigned_to_resource_type: "load_balancer",
                    assigned_to: uuid,
                },
            });
        } else if (assignedPublicIp) {
            await unAssignIpMutation.mutateAsync({ location, address: assignedPublicIp.address });
        }
    }

    return (
        <DetailsTableIpAddress
            value={assignedPublicIp}
            location={location}
            onChange={async (obj) => await assignIpAddress(obj)}
        />
    );
}

function TargetServersContent({ item }: { item: LoadBalancerWithType }) {
    //#region Hooks
    const { t } = useLingui();
    const unlinkTargetMutation = useStandardMutation(unlinkTargetFromLoadBalancerMutation);
    const [isAdding, setIsAdding] = useState(false);

    const { data: targetResources } = useQueryResultAtom(lbTargetsAtom(item.location));
    //#endregion

    const { location, uuid } = item;

    async function unlinkTarget(target_uuid: string) {
        await unlinkTargetMutation.mutateAsync({ location, uuid, target_uuid });
    }

    return (
        <ContentPane className="VStack gap-4">
            <SectionHeader title={<Trans>Target servers</Trans>} />

            <WTable
                afterTable={
                    !isAdding && (
                        <WButton
                            color="primary"
                            variant="border"
                            size="bar"
                            icon="jp-icon-add"
                            action={() => setIsAdding(!isAdding)}
                        >
                            <Trans>Add target server</Trans>
                        </WButton>
                    )
                }
            >
                <thead>
                    <tr>
                        <th>
                            <Trans>Name</Trans>
                        </th>
                        <th>
                            <Trans>Type</Trans>
                        </th>
                        <th>
                            <Trans>Private IP</Trans>
                        </th>
                        <th>
                            <Trans>Status</Trans>
                        </th>
                        <th />
                    </tr>
                </thead>
                <WTableBody>
                    {item.targets.map((item) => {
                        const resource = targetResources?.get(item.target_uuid);
                        const ip = resource?.private_ipv4;

                        return (
                            <tr key={item.target_uuid}>
                                <td>
                                    {resource && <ResourceLink title={getResourceName(resource)} item={resource} />}
                                </td>
                                <td>
                                    <ResourceWithIcon type={convertTargetType(item.target_type)} />
                                </td>
                                <td>{!!ip && <ClipBoardTooltip>{ip}</ClipBoardTooltip>}</td>
                                <td>{!!resource && <ResourceStatusBadge item={resource} />}</td>
                                <td className="text-right">
                                    <DeleteButton
                                        label={t`Unlink target`}
                                        inTable
                                        action={async () => await unlinkTarget(item.target_uuid)}
                                    />
                                </td>
                            </tr>
                        );
                    })}
                </WTableBody>
                {isAdding && (
                    <tfoot>
                        <tr>
                            <td colSpan={5}>
                                <AddTargetServerForm item={item} onClose={() => setIsAdding(false)} />
                            </td>
                        </tr>
                    </tfoot>
                )}
            </WTable>
        </ContentPane>
    );
}

function ForwardingRulesContent({ item }: { item: LoadBalancerWithType }) {
    const dropForwardingRuleMutation = useStandardMutation(dropForwardingRuleFromLoadBalancerMutation);

    const { location, uuid } = item;
    const [isAdding, setIsAdding] = useState(false);

    async function dropRule(rule_uuid: string) {
        await dropForwardingRuleMutation.mutateAsync({ location, uuid, rule_uuid });
    }

    return (
        <ContentPane className="VStack gap-4">
            <SectionHeader title={<Trans>Forwarding rules</Trans>} />

            <WTable
                afterTable={
                    !isAdding && (
                        <WButton
                            color="primary"
                            variant="border"
                            size="bar"
                            icon="jp-icon-add"
                            action={() => setIsAdding(!isAdding)}
                        >
                            <Trans>Add new rule</Trans>
                        </WButton>
                    )
                }
            >
                <thead>
                    <tr>
                        <th>
                            <Trans>Protocol</Trans>
                        </th>
                        <th className="text-right">
                            <Trans>Port</Trans>
                        </th>
                        <th />
                        <th>
                            <Trans>Target port</Trans>
                        </th>
                        <th />
                    </tr>
                </thead>
                <WTableBody>
                    {item.forwarding_rules.map((item) => (
                        <tr key={item.uuid}>
                            <td>TCP</td>
                            <td className="text-right">{item.source_port}</td>
                            <td className="text-center">
                                <MaskIcon className="jp-arrow-thin-right-icon size-1.25rem color-muted" />
                            </td>
                            <td>{item.target_port}</td>
                            <td className="text-right">
                                <DeleteButton inTable action={async () => await dropRule(item.uuid)} />
                            </td>
                        </tr>
                    ))}
                </WTableBody>
                {isAdding && (
                    <tfoot>
                        <tr aria-label="Add forwarding rule">
                            <AddForwardingRuleForm item={item} onClose={() => setIsAdding(false)} />
                        </tr>
                    </tfoot>
                )}
            </WTable>
        </ContentPane>
    );
}
